import { ICompileService } from "angular";
import {
  FieldConfigurationHelperService,
  vsName as FieldConfigurationHelperServiceName,
} from "../../../records/forms/formly/fieldConfigurationHelpers";
import {
  FormViewConfig,
  FormViewService,
  FormViewServiceProvider,
  vsServiceName as FormViewServiceName,
  vsProviderName as FormViewServiceProviderName,
  FormSubmitArgs,
} from "../../../records/forms/formView";
import { RecordDataSource } from "../../../records/grids";
import {
  ColumnDefinitionService,
  vsName as ColumnDefinitionServiceName,
} from "../../../records/grids/columns/columnDefinitionService";
import { FilterProvider } from "../../../records/grids/columns/filters/FilterProvider";
import { LookupFilter } from "../../../records/grids/columns/filters/subFilters/LookupFilter";
import {
  ServerUtility,
  vsName as ServerUtilityName,
} from "../../../records/server/serverUtility";
import {
  GridConfiguration,
  RecordGridViewServiceProvider,
  vsProviderName as RecordGridViewServiceProviderName,
} from "../../../records/views/recordGridView";
import fieldHelpers from "../../../ui-formly/fieldHelpers";
import { vsLookupManagementServiceProviderName as LookupManagementServiceProviderName } from "../../shared/lookups";
import {
  LookupManagementConfig,
  LookupManagementServiceProvider,
} from "../../../records/lookup/lookupManagement";
import { MachineModelType, MachineModelView } from "../models/machine";

export const vsObjectName = "machineModelType";

const url = "/api/machines/MachineModelType";

MachineModelTypeConfig.$inject = [
  FormViewServiceProviderName,
  RecordGridViewServiceProviderName,
  LookupManagementServiceProviderName,
];
export function MachineModelTypeConfig(
  formViewServiceProvider: FormViewServiceProvider,
  recordGridViewServiceProvider: RecordGridViewServiceProvider,
  lookupManagementServiceProvider: LookupManagementServiceProvider
) {
  recordGridViewServiceProvider.addGridConfiguration(
    vsObjectName,
    getGridConfig
  );
  formViewServiceProvider.addFormViewConfig(vsObjectName, getFormConfig);

  lookupManagementServiceProvider.addLookup(vsObjectName, {
    displayName: "Model Type",
    config: [
      ServerUtilityName,
      (serverUtility: ServerUtility) => {
        const config: LookupManagementConfig = {
          fetchFormModel(id) {
            return serverUtility.querySingle({
              url,
              query: { Id: id },
            });
          },
          baseUrl: url,
          getLongItemDisplay: (modelType: MachineModelView) =>
            `${modelType.MachineManufacturerName}-${modelType.Name}`,
        };
        return config;
      },
    ],
  });
}

getGridConfig.$inject = [
  ColumnDefinitionServiceName,
  "$compile",
  ServerUtilityName,
];
function getGridConfig(
  columnDefinitionService: ColumnDefinitionService,
  $compile: ICompileService,
  serverUtility: ServerUtility
): GridConfiguration {
  const config: GridConfiguration = {
    columns: [
      columnDefinitionService.getNumberColDef({ field: "Id", hide: true }),
      columnDefinitionService.getStringColDef({
        field: "MachineManufacturerName",
        header: "Manufacturer",
        filter: new FilterProvider("MachineManufacturerName", $compile)
          .defaultStringFilters()
          .addFilter(
            "=",
            new LookupFilter({
              displayName: "=",
              queryFieldNames: ["MachineManufacturerId"],
              serverQuery: (search) =>
                serverUtility.lookupQuery({
                  url: "/api/machines/MachineManufacturer",
                  search,
                }),
            })
          )
          .getGridFilterFactory(),
      }),
      columnDefinitionService.getStringColDef({
        field: "Name",
        header: "Model Type",
        filter: new FilterProvider("Name", $compile)
          .defaultStringFilters()
          .getGridFilterFactory(),
      }),
      columnDefinitionService.getBooleanColDef({
        field: "Disabled",
      }),
    ],
    gridDataSource: new RecordDataSource(serverUtility, url),
    defaultSort: "MachineManufacturerName,Name",
    namePlural: "Model Types",
    nameSingular: "Model Type",
    showAddButton: true,
    showAddButtonPermission: "machineLookupModify",
    showSelectButtons: false,
  };
  return config;
}

getFormConfig.$inject = [
  FormViewServiceName,
  FieldConfigurationHelperServiceName,
];
function getFormConfig(
  formViewService: FormViewService,
  fieldConfigurationHelperService: FieldConfigurationHelperService
): FormViewConfig {
  return {
    fields: [
      fieldConfigurationHelperService.selectAsyncField({
        key: "MachineManufacturerId",
        name: "MachineManufacturerId",
        label: "Manufacturer",
        required: true,
        getDefaultItem: (model) => {
          return {
            Id: model.MachineManufacturerId,
            Name: model.MachineManufacturerName,
          };
        },
        queryUrl: "/api/machines/MachineManufacturer",
      }),
      fieldHelpers.simpleStringField({
        key: "Name",
        label: "Model Type",
        required: true,
      }),
      fieldHelpers.simpleBoolField({
        key: "Disabled",
        defaultValue: false,
      }),
    ],
    title: (model) => (model.Id ? "Edit Model Type" : "Create Model Type"),
    name: vsObjectName,
    submitForm: (args: FormSubmitArgs) => {
      const model = <MachineModelType>args.model;
      return formViewService.saveForm({
        formObject: model,
        formViewController: args.formViewController,
        url,
      });
    },
  };
}
